import type { ReactNode } from 'react';
import { Button } from './Button';
import { Icon, type IconName } from './Icon';
import { Skeleton } from './Spinner';
import { cn } from './cn';

type Tone = 'brand' | 'danger';

const TONES: Record<
  Tone,
  { glow: string; ring: string; tile: string; spark: string; border: string }
> = {
  brand: {
    glow: 'from-turquoise/15 via-transparent to-transparent',
    ring: 'border-turquoise/25 bg-turquoise/5',
    tile: 'bg-gradient-to-br from-primary-light to-secondary-light text-navy ring-1 ring-turquoise/20',
    spark: 'bg-mint',
    border: 'border-line',
  },
  danger: {
    glow: 'from-danger/12 via-transparent to-transparent',
    ring: 'border-danger/25 bg-danger/5',
    tile: 'bg-danger-light text-danger ring-1 ring-danger/20',
    spark: 'bg-danger',
    border: 'border-danger/25',
  },
};

/**
 * Shared layout for empty / error screens: a soft card, an icon tile with two faint rings around it,
 * a short title, a calm description and the actions (full width on phones, side by side from sm up).
 * Icons only — no illustrations to download.
 */
function StateCard({
  tone,
  icon,
  title,
  description,
  children,
  role,
}: {
  tone: Tone;
  icon: IconName;
  title: string;
  description?: string;
  children?: ReactNode;
  role?: 'alert';
}) {
  const t = TONES[tone];
  return (
    <div
      role={role}
      className={cn(
        'animate-rise bg-paper shadow-card relative isolate flex flex-col items-center overflow-hidden rounded-[24px] border px-5 pt-9 pb-7 text-center sm:px-8 sm:pt-11 sm:pb-9',
        t.border,
      )}
    >
      {/* soft light from the top */}
      <div
        className={cn(
          'pointer-events-none absolute inset-x-0 top-0 -z-10 h-44 bg-gradient-to-b',
          t.glow,
        )}
        aria-hidden
      />

      <div className="relative mb-6 grid size-28 place-items-center" aria-hidden>
        <span className={cn('absolute inset-0 rounded-full border', t.ring)} />
        <span className={cn('absolute inset-4 rounded-full border', t.ring)} />
        <span className={cn('absolute -end-0.5 top-4 size-2 rounded-full opacity-70', t.spark)} />
        <span
          className={cn('absolute -start-1 bottom-6 size-1.5 rounded-full opacity-50', t.spark)}
        />
        <span
          className={cn(
            'animate-float shadow-card relative grid size-[68px] place-items-center rounded-[22px]',
            t.tile,
          )}
        >
          <Icon name={icon} size={32} />
        </span>
      </div>

      <h3 className="text-lg leading-8 font-bold text-balance">{title}</h3>
      {description && (
        <p className="text-ink-2 mt-1.5 max-w-sm text-sm leading-7 whitespace-pre-line">
          {description}
        </p>
      )}
      {children && (
        <div className="mt-6 flex w-full max-w-xs flex-col items-stretch gap-2.5 sm:w-auto sm:max-w-none sm:flex-row sm:justify-center">
          {children}
        </div>
      )}
    </div>
  );
}

export function ErrorState({
  message,
  onRetry,
  title = 'مشکلی پیش آمده است',
  icon = 'alert',
}: {
  message?: string;
  onRetry?: () => void;
  title?: string;
  icon?: IconName;
}) {
  return (
    <StateCard tone="danger" icon={icon} title={title} description={message} role="alert">
      {onRetry && (
        <Button variant="outline" size="md" onClick={onRetry} className="min-w-36">
          تلاش مجدد
        </Button>
      )}
    </StateCard>
  );
}

/**
 * Empty list or first-visit prompt.
 * `image` is kept so existing callers still compile, but it is no longer drawn: pick an `icon` instead.
 */
export function EmptyState({
  title,
  description,
  action,
  icon = 'box',
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  /** @deprecated ignored — use `icon` */ image?: string;
  icon?: IconName;
}) {
  return (
    <StateCard tone="brand" icon={icon} title={title} description={description}>
      {action}
    </StateCard>
  );
}

export function ListSkeleton({ rows = 4, height = 'h-28' }: { rows?: number; height?: string }) {
  return (
    <div className="flex flex-col gap-3" aria-busy="true" aria-label="در حال بارگذاری">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className={`${height} w-full rounded-[16px]`} />
      ))}
    </div>
  );
}

export function FullPageSpinner() {
  return (
    <div className="bg-page grid min-h-[60dvh] place-items-center">
      <div
        className="relative grid size-14 place-items-center"
        role="status"
        aria-label="در حال بارگذاری"
      >
        <span className="border-turquoise/15 absolute inset-0 rounded-full border-4" />
        <span className="border-t-turquoise absolute inset-0 animate-spin rounded-full border-4 border-transparent" />
        <span className="animate-live bg-mint size-2.5 rounded-full" />
      </div>
    </div>
  );
}
